import { ModelDataType } from 'src/app/interfaces/model-data-type';

import { BaseModel } from './base-model';
import { File as ViveFile } from './file';
import { PrimaryTrade } from './primary-trade';
import { TradeLicense } from './trade-license';
import { Vendor } from './vendor';

export class License extends BaseModel {
  static override columnMap = new Map<
    string,
    { property: string; type: 'column'; dataType: ModelDataType }
  >();
  static override associationMap = new Map<
    string,
    { property: string; type: 'association'; model: string }
  >();
  static override aliasedColumnMap = new Map<
    string,
    { property: string; type: 'alias' }
  >();

  static LicenseProperties: (
    | {
        property: keyof License;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof License;
        type: 'alias';
      }
    | {
        property: keyof License;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'VendorId', type: 'column', dataType: 'number' },
    { property: 'licenseNumber', type: 'column', dataType: 'string' },
    { property: 'licenseState', type: 'column', dataType: 'string' },
    { property: 'status', type: 'column', dataType: 'string' },
    { property: 'trade', type: 'column', dataType: 'string' },
    { property: 'PrimaryTradeId', type: 'column', dataType: 'number' },
    { property: 'classification', type: 'column', dataType: 'string' },
    { property: 'description', type: 'column', dataType: 'string' },
    { property: 'issueDate', type: 'column', dataType: 'date' },
    { property: 'expirationDate', type: 'column', dataType: 'date' },
    { property: 'name', type: 'column', dataType: 'string' },
    { property: 'qualifyingMaster', type: 'column', dataType: 'string' },
    { property: 'businessName', type: 'column', dataType: 'string' },
    { property: 'businessAddress1', type: 'column', dataType: 'string' },
    { property: 'businessAddress2', type: 'column', dataType: 'string' },
    { property: 'businessCity', type: 'column', dataType: 'string' },
    { property: 'businessState', type: 'column', dataType: 'string' },
    { property: 'businessZip', type: 'column', dataType: 'string' },
    { property: 'businessPhone', type: 'column', dataType: 'string' },
    { property: 'county', type: 'column', dataType: 'string' },
    {
      property: 'complaintViolationInformation',
      type: 'column',
      dataType: 'string'
    },
    { property: 'citationViolation', type: 'column', dataType: 'string' },
    { property: 'citationViolationDate', type: 'column', dataType: 'date' },
    { property: 'citationStatus', type: 'column', dataType: 'string' },
    { property: 'citationCode', type: 'column', dataType: 'string' },
    {
      property: 'bondingInsuranceCompanyName',
      type: 'column',
      dataType: 'string'
    },
    { property: 'bondingNumber', type: 'column', dataType: 'string' },
    { property: 'bondingAmount', type: 'column', dataType: 'number' },
    { property: 'bondEffectiveDate', type: 'column', dataType: 'date' },
    { property: 'bondCancellationDate', type: 'column', dataType: 'date' },
    { property: 'bondCompanyCode', type: 'column', dataType: 'string' },
    { property: 'bondAgencyAddress', type: 'column', dataType: 'string' },
    { property: 'bondAgencyPhoneNumber', type: 'column', dataType: 'string' },
    { property: 'workCompInsuranceAgency', type: 'column', dataType: 'string' },
    { property: 'workCompPolicyNumber', type: 'column', dataType: 'string' },
    { property: 'workCompExemption', type: 'column', dataType: 'string' },
    { property: 'workCompEffectiveDate', type: 'column', dataType: 'date' },
    { property: 'workCompExpirationDate', type: 'column', dataType: 'date' },
    { property: 'personnelList', type: 'column', dataType: 'string' },
    { property: 'personnelName', type: 'column', dataType: 'string' },
    { property: 'personnelTitle', type: 'column', dataType: 'string' },
    { property: 'associationDate', type: 'column', dataType: 'date' },
    { property: 'personnelCode', type: 'column', dataType: 'string' },
    { property: 'licenseFile', type: 'column', dataType: 'string' },
    { property: 'LicenseFileId', type: 'column', dataType: 'number' },
    { property: 'licenseFileName', type: 'column', dataType: 'string' },
    { property: 'imageVerifiedLicense', type: 'column', dataType: 'boolean' },
    { property: 'onlineVerifiedLicense', type: 'column', dataType: 'boolean' },
    {
      property: 'onlineVerifiedLicenseSource',
      type: 'column',
      dataType: 'string'
    },
    {
      property: 'nonLicenseCertification',
      type: 'column',
      dataType: 'boolean'
    },
    { property: 'municipalityName', type: 'column', dataType: 'string' },
    { property: 'licensorRegion', type: 'column', dataType: 'string' },
    { property: 'verificationType', type: 'column', dataType: 'string' },
    { property: 'licenseOwner', type: 'column', dataType: 'string' },
    { property: 'createdAt', type: 'column', dataType: 'date' },
    { property: 'updatedAt', type: 'column', dataType: 'date' },

    { property: 'TradeLicenses', model: 'TradeLicense', type: 'association' },
    { property: 'Vendor', model: 'Vendor', type: 'association' },
    { property: 'PrimaryTrade', model: 'PrimaryTrade', type: 'association' }
  ];

  static {
    License.LicenseProperties.forEach((propertyObject) => {
      const { property, type } = propertyObject;
      if (type === 'column') {
        License.columnMap.set(property, propertyObject);
      }
      if (type === 'alias') {
        License.aliasedColumnMap.set(
          property,
          propertyObject as { property: string; type: 'alias' }
        );
      }
      if (type === 'association') {
        License.associationMap.set(
          property,
          propertyObject as {
            property: string;
            type: 'association';
            model: string;
          }
        );
      }
    });
  }

  constructor(LicenseObject: Partial<License>) {
    super();

    License.LicenseProperties.forEach((propertyObject) => {
      const { property } = propertyObject;
      const value = LicenseObject[property];
      if (value !== undefined) {
        Object.assign(this, { [property]: value });
      }
    });
  }

  id?: number;
  VendorId?: number;
  licenseNumber?: string | null;
  licenseState?: string;
  status?: null | 'Active' | 'Revoked' | 'Lapsed' | 'Archived';
  trade?: string | null;
  PrimaryTradeId?: number | null;
  classification?: string | null;
  description?: string | null;
  issueDate?: Date | null;
  expirationDate?: Date | null;
  name?: string;
  qualifyingMaster?: string | null;
  businessName?: string | null;
  businessAddress1?: string | null;
  businessAddress2?: string | null;
  businessCity?: string | null;
  businessState?: string | null;
  businessZip?: string | null;
  businessPhone?: string | null;
  county?: string | null;
  complaintViolationInformation?: string | null;
  citationViolation?: string | null;
  citationViolationDate?: Date | null;
  citationStatus?: string | null;
  citationCode?: string | null;
  bondingInsuranceCompanyName?: string | null;
  bondingNumber?: string | null;
  bondingAmount?: number | null;
  bondEffectiveDate?: Date | null;
  bondCancellationDate?: Date | null;
  bondCompanyCode?: string | null;
  bondAgencyAddress?: string | null;
  bondAgencyPhoneNumber?: string | null;
  workCompInsuranceAgency?: string | null;
  workCompPolicyNumber?: string | null;
  workCompExemption?: string | null;
  workCompEffectiveDate?: Date | null;
  workCompExpirationDate?: Date | null;
  personnelList?: string | null;
  personnelName?: string | null;
  personnelTitle?: string | null;
  associationDate?: Date | null;
  personnelCode?: string | null;
  licenseFile?: string;
  LicenseFileId?: number;
  licenseFileName?: string;
  imageVerifiedLicense?: boolean | null;
  onlineVerifiedLicense?: boolean | null;
  onlineVerifiedLicenseSource?: string | null;
  nonLicenseCertification?: boolean | null;
  municipalityName?: string | null;
  licensorRegion?: null | 'State' | 'Municipality' | 'Both';
  verificationType?:
    null | 'Online Verified' | 'Image Verified' | 'Non-License / Certificate';
  licenseOwner?: null | 'Corporate' | 'Owner' | 'Employee';
  createdAt?: Date;
  updatedAt?: Date;

  TradeLicenses?: TradeLicense[];
  Vendor?: Vendor;
  PrimaryTrade?: PrimaryTrade;
  LicenseFile?: ViveFile;
}
